<template>
  <div id="page-order-edit">
    <vs-alert
      color="danger"
      title="Order Not Found"
      :active.sync="orderNotFound"
    >
      <span>
        Order record with id
        <strong>{{ $route.params.orderId }}</strong> not found.
      </span>
      <span>
        <span>Check</span>
        <router-link :to="{ name: 'order-list' }" class="text-inherit underline"
          >All Orders</router-link
        >
      </span>
    </vs-alert>
    <dx-tab-panel v-if="localData">
      <dx-item :title="$t(`Order.General`)" template="general-tab" />
      <template #general-tab>
        <vx-card class="mb-base">
          <dx-validation-group ref="validation-group">
            <div class="dx-fieldset">
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.Description") }}
                </div>
                <div class="dx-field-value">
                  <DxTextBox :value.sync="localData.description">
                    <DxValidator>
                      <DxRequiredRule
                        :message="$t('Order.DescriptionRequired')"
                      />
                    </DxValidator>
                  </DxTextBox>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.OrderNumber") }}
                </div>
                <div class="dx-field-value">
                  <DxTextBox :value.sync="localData.orderNumber"> </DxTextBox>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.CustomerReference") }}
                </div>
                <div class="dx-field-value">
                  <DxTextBox :value.sync="localData.customerReference">
                  </DxTextBox>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.State") }}
                </div>
                <div class="dx-field-value">
                  <DxSelectBox
                    :items="orderStates"
                    :search-enabled="true"
                    :value.sync="localData.state"
                    display-expr="description"
                    value-expr="id"
                  >
                  </DxSelectBox>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.ProposalDate") }}
                </div>
                <div class="dx-field-value">
                  <DxDateBox :value.sync="localData.proposalDate"> </DxDateBox>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.ProposalValidUntil") }}
                </div>
                <div class="dx-field-value">
                  <DxDateBox :value.sync="localData.proposalValidUntil">
                  </DxDateBox>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.OrderDate") }}
                </div>
                <div class="dx-field-value">
                  <DxDateBox :value.sync="localData.orderDate"> </DxDateBox>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.Amount") }}
                </div>
                <div class="dx-field-value">
                  <div class="total">
                    {{ localData.amountGross | currency }}
                  </div>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.AmountVAT") }}
                </div>
                <div class="dx-field-value">
                  <div class="total">{{ localData.amountVat | currency }}</div>
                </div>
              </div>
              <div class="dx-field">
                <div class="dx-field-label">
                  {{ $t("Order.OrderTotal") }}
                </div>
                <div class="dx-field-value">
                  <div class="total order-total">
                    {{ localData.amountTotal | currency }}
                  </div>
                </div>
              </div>
            </div>
          </dx-validation-group>
        </vx-card>
      </template>
      <dx-item :title="$t('Order.Notes')" template="notes-tab" />
      <template #notes-tab>
        <vx-card class="mb-base">
          <div class="left-content">
            <DxTextArea :height="300" :value.sync="localData.notes" />
          </div>
        </vx-card>
      </template>
    </dx-tab-panel>
    <vx-card :title="$t('Order.OrderLines')">
      <DxList
        :data-source="localData.lines"
        height="100%"
        :allow-item-deleting="allowItemDeleting"
        item-delete-mode="static"
        :hoverStateEnabled="false"
      >
        <template #item="{ data: item }">
          <order-edit-line
            :template-data="item"
            :show-labels="showLabels(item)"
            @values-changed="valuesChanged"
          ></order-edit-line>
        </template>
        <DxItemDragging
          :allow-reordering="true"
          :on-reorder="onReorder"
          :data="localData.lines"
        />
      </DxList>
      <div>
        <vs-button @click="startAdd" color="primary" type="border" icon="add">{{
          $t("OrderLine.New")
        }}</vs-button>
      </div>
    </vx-card>
    <div class="vx-row">
      <div class="vx-col w-full">
        <div class="mt-8 flex flex-wrap items-center justify-end">
          <vs-button
            class="ml-auto mt-2"
            :disabled="!canSave"
            @click="saveChanges"
            >{{ $t("Save") }}</vs-button
          >
          <vs-button
            class="ml-4 mt-2"
            type="border"
            color="warning"
            @click="cancel"
            >{{ $t("Cancel") }}</vs-button
          >
        </div>
      </div>
    </div>
  </div>
</template>

<script>

// Store Module
import moduleOrderManagement from '@/store/order-management/moduleOrderManagement.js'
import { DxSelectBox } from 'devextreme-vue/select-box'
import { DxTextBox } from 'devextreme-vue/text-box'
import { DxCheckBox } from 'devextreme-vue/check-box'
import { DxDateBox } from 'devextreme-vue/date-box'
import DxTextArea from 'devextreme-vue/text-area';
import DxValidationGroup from 'devextreme-vue/validation-group'
import DxTagBox from 'devextreme-vue/tag-box';
import DxList, { DxItemDragging } from 'devextreme-vue/list'
import { DxTabPanel, DxItem } from 'devextreme-vue/tab-panel'
import {
  DxValidator,
  DxRequiredRule
} from 'devextreme-vue/validator'

import { notifyMixin } from '@/mixins/notifyMixin'
import OrderEditLine from './OrderEditLine'
import { mapGetters } from 'vuex'
export default {
  mixins: [notifyMixin],
  data () {
    const orderStates = this.$store.state.refCodes.orderStates.map(a => {
      return { ...a, description: this.$t(`OrderState.${a.description}`) }
    })
    return {
      localData: {},
      orderNotFound: false,
      newId: 0,
      isNew: false,
      orderStates
    }
  },
  components: {
    DxList,
    DxItemDragging,
    DxSelectBox,
    DxTextBox,
    DxCheckBox,
    DxValidator,
    DxValidationGroup,
    DxRequiredRule,
    DxDateBox,
    DxTagBox,
    OrderEditLine,
    DxTabPanel,
    DxItem,
    DxTextArea
  },
  methods: {
    async fetchData (orderId) {
      try {
        this.isNew = orderId ? false : true
        const res = !this.isNew ? await this.$store.dispatch('orderManagement/fetchOrder', orderId) : await this.$store.dispatch('orderManagement/fetchNewOrder', this.activeOrganisation.id)
        this.localData = res.data.order
        this.localData.idOrganisation = this.activeOrganisation.id
        this.localData.lines = res.data.order.lines.map(a => { return { ...a, isValid: true } })
        if (this.localData === null) {
          this.orderNotFound = true
          return
        }
      }
      catch (err) {
        if (err.response && err.response.status === 404) {
          this.orderNotFound = true
          return
        }
        this.notifyError(err)
      }
    },
    async saveChanges () {
      const validationGroup = this.$refs["validation-group"].instance
      const result = validationGroup.validate()
      if (!result.isValid) {
        return
      }
      try {
        const res = await this.$store.dispatch('orderManagement/saveOrder', this.localData)
        this.$router.push(`/order/view/${res.data.order.id}`)
        this.notifySuccess(this.isNew ? this.$t("Order.Added") : this.$t("Order.Updated"))
      } catch (err) {
        this.notifyError(err)
      }
    },
    valuesChanged (val) {
      const lines = this.localData.lines
      const line = lines.find(a => a.id == val.id)
      if (line) {
        line.idProduct = val.idProduct
        line.description = val.description
        line.quantity = val.quantity
        line.pricePerUnit = val.pricePerUnit
        line.startDate = val.startDate
        line.endDate = val.endDate
        line.isValid = val.isValid
      }
      let amountGross = 0
      for (let i = 0; i < lines.length; i++) {
        amountGross += (lines[i].quantity * lines[i].pricePerUnit)
      }
      this.localData.amountGross = amountGross
      this.localData.amountVat = amountGross * this.localData.vatPercentage
      this.localData.amountTotal = amountGross + this.localData.amountVat
    },
    cancel () {
      this.$router.push(this.isNew ? "/order/list" : `/order/view/${this.localData.id}`)
    },
    showLabels (item) {
      return this.localData.lines.indexOf(item) == 0
    },
    startAdd () {
      this.newId++
      this.localData.lines.push({ id: `NEW-${this.newId}`, idProduct: 1, description: '', startDate: null, endDate: null, quantity: 1, pricePerUnit: 0, isValid: false })
    },
    onReorder (e) {
      this.localData.lines.splice(e.toIndex, 0, this.localData.lines.splice(e.fromIndex, 1)[0])
    }
  },
  computed: {
    ...mapGetters(["activeOrganisation"]),
    allowItemDeleting () {
      return this.localData && this.localData.lines && this.localData.lines.length > 1
    },
    canSave () {
      return this.localData.lines && this.localData.lines.length > 0 & !this.localData.lines.find(a => !a.isValid)
    }
  },
  async created () {
    if (!moduleOrderManagement.isRegistered) {
      this.$store.registerModule('orderManagement', moduleOrderManagement)
      moduleOrderManagement.isRegistered = true
    }

    await this.fetchData(this.$route.params.orderId)
  },
}

</script>

<style scoped>
.total {
  font-weight: bold;
  font-size: largee;
  width: 100%;
  text-align: right;
}

.order-total {
  font-size: large;
  color: blue;
}
</style>

